import cx from 'classnames'
import styles from './SelectorSwitch.module.css'

export interface SelectorSwitchOption<Value extends string> {
  /** The value this option represents */
  value: Value
  /** Text shown on the option button */
  label: string
}

export interface SelectorSwitchProps<Value extends string> {
  /** The selectable options, in display order */
  options: SelectorSwitchOption<Value>[]
  /** The currently selected value */
  value: Value
  /** Called with the new value when an option is selected */
  onChange: (value: Value) => void
  /** Label shown under the options */
  label?: string
  /** Accessible label for the option group */
  ariaLabel?: string
  /** Additional CSS class */
  className?: string
}

/**
 * A multi-way mode selector styled like a chicken-head
 * rotary switch, e.g. for choosing an effect voicing.
 */
export function SelectorSwitch<Value extends string>({
  options,
  value,
  onChange,
  label,
  ariaLabel,
  className,
}: SelectorSwitchProps<Value>) {
  return (
    <div
      className={cx(styles.SelectorSwitch, className)}
      role="radiogroup"
      aria-label={ariaLabel ?? label}
    >
      <div className={styles.SelectorSwitch__options}>
        {options.map((option) => {
          const isActive = option.value === value
          return (
            <button
              key={option.value}
              className={cx(styles.SelectorSwitch__option, {
                [styles.isActive]: isActive,
              })}
              type="button"
              role="radio"
              aria-checked={isActive}
              onClick={() => onChange(option.value)}
            >
              {option.label}
            </button>
          )
        })}
      </div>
      {label && (
        <span className={styles.SelectorSwitch__label}>
          {label}
        </span>
      )}
    </div>
  )
}
